/**
 * The plumbing every importer shares: the audit trail, the frontmatter it may
 * write, and the two text problems that break a directive if you get them wrong.
 *
 * Extracted from `gitbook.ts` when the second importer arrived. The rule it
 * encodes is the one that matters: convert what we model, preserve what we do
 * not, and REPORT everything either way. A migration is a one-way door, so an
 * import nobody can audit is an import nobody should run.
 *
 * Three facts here were MEASURED against the pipeline in `canonical.ts`, not
 * assumed. Each one silently damages a page if ignored:
 *
 * 1. A `"` inside a directive attribute must be written as `&#x22;`.
 *    `&quot;` parses, but canonicalise re-emits it as `&#x22;`, so the
 *    importer's own output would not be canonical. A raw `\"` is worse: the
 *    directive stops being a directive and the page gains a literal `\:::`.
 * 2. A newline inside an attribute value has the same effect. Attribute values
 *    must be single-line, so whitespace is collapsed.
 * 3. Frontmatter keys outside the seven in `@staleless/schema` are DROPPED by
 *    canonicalise, without an error. So an importer may not "pass through"
 *    `sidebar_label` or `icon` and hope: an unmapped key has to be reported.
 *    A fractional `order` is worse than dropped — the schema requires an
 *    integer, so canonicalise throws.
 */
import { parse as parseYaml } from 'yaml';
export function createNotes() {
    const notes = new Map();
    const note = (kind, rule, message, times = 1) => {
        if (times <= 0)
            return;
        const existing = notes.get(rule);
        if (existing)
            existing.count += times;
        else
            notes.set(rule, { kind, rule, message, count: times });
    };
    return { note, list: () => [...notes.values()] };
}
/**
 * Make a string safe as a directive attribute value.
 *
 * See fact 1 and fact 2 in the header: the entity has to be the numeric one,
 * and the value has to stay on one line.
 */
export function quoteAttr(value) {
    return value.replace(/\s+/g, ' ').trim().replace(/"/g, '&#x22;');
}
/** Quote a YAML scalar only when it needs it. */
export function yamlScalar(value) {
    return /^[\w][\w .,'()/-]*$/.test(value) && !value.includes(': ')
        ? value
        : `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
}
/**
 * Truncate without splitting a word.
 *
 * `slice()` alone produced a GitBook description ending "...can be fr", and
 * because the body then had that prefix removed, the page opened with
 * "ustrating.".
 */
export function truncateAtWord(value, max) {
    if (value.length <= max)
        return value;
    const cut = value.slice(0, max);
    const lastSpace = cut.lastIndexOf(' ');
    return `${(lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}
/**
 * Derive a description from the body, and remove the sentence it came from.
 *
 * The site renders the description as a visible lede under the title, so a
 * description lifted verbatim from the opening sentence would print that
 * sentence twice on every imported page. Where the paragraph provably BEGINS
 * with the description, the prefix is dropped: the reader loses nothing,
 * because the words are still on the page in the lede.
 *
 * Only a WHOLE sentence may be removed. Cutting a sentence in half and deleting
 * the half leaves the remainder as gibberish.
 */
export function deriveDescription(body, note) {
    const firstParagraph = body
        .split(/\n{2,}/)
        .map((block) => block.trim())
        .find((block) => block.length > 0 &&
        !block.startsWith('#') &&
        /*
         * Any directive, not only a container's `:::`. A page can OPEN with a
         * shared block — `::snippet{name="…"}` — and a description derived
         * from that says `::snippet{name="abbreviations"}` in the search
         * results and in every social card.
         */
        !block.startsWith('::') &&
        !block.startsWith('>') &&
        !block.startsWith('<') &&
        !block.startsWith('*') &&
        !block.startsWith('|'));
    let description;
    let descriptionIsWholeSentence = false;
    if (firstParagraph) {
        const flat = firstParagraph.replace(/\s+/g, ' ').replace(/[*_`]/g, '').trim();
        // 200, not 160. A real GitBook page opens with a 162-character sentence, and
        // a tighter bound sent it down the truncation path — which then cut mid-word
        // and left "ustrating." as the opening line of the page.
        const sentence = flat.match(/^(.{40,200}?[.!?])(?:\s|$)/);
        if (sentence) {
            description = sentence[1].trim();
            descriptionIsWholeSentence = true;
        }
        else {
            description = truncateAtWord(flat, 155);
        }
        note('derived', 'description', 'The description was derived from the first paragraph and should be reviewed.');
    }
    else {
        note('review', 'no-description', 'No paragraph to derive a description from.');
    }
    let finalBody = body;
    if (description && firstParagraph && descriptionIsWholeSentence) {
        const plain = (v) => v.replace(/\s+/g, ' ').replace(/[*_`]/g, '').trim();
        if (plain(firstParagraph).startsWith(description)) {
            const remainder = plain(firstParagraph).slice(description.length).trim();
            finalBody = remainder
                ? body.replace(firstParagraph, remainder)
                : body.replace(firstParagraph, '').replace(/^\n+/, '');
            note('review', 'lede-deduplicated', 'The opening sentence became the description, so it was removed from the body to avoid printing it twice.');
        }
    }
    return { ...(description ? { description } : {}), body: finalBody };
}
/**
 * Split a page into its YAML frontmatter and its body.
 *
 * Malformed YAML is REPORTED, never thrown: a page whose frontmatter does not
 * parse still has a body, and refusing to import it would lose the body too.
 */
export function splitFrontmatter(text, note) {
    const match = text.match(/^---\r?\n([\s\S]*?)\r?\n---[ \t]*(?:\r?\n|$)/);
    if (!match)
        return { data: {}, body: text };
    const body = text.slice(match[0].length);
    try {
        const parsed = parseYaml(match[1]);
        if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
            note('review', 'frontmatter-not-a-map', 'The frontmatter was not a map of keys, so it was ignored.');
            return { data: {}, body };
        }
        return { data: parsed, body };
    }
    catch {
        note('review', 'frontmatter-unparseable', 'The frontmatter is not valid YAML. It was left out of the imported page and needs to be re-entered by hand.');
        return { data: {}, body };
    }
}
/**
 * Report the frontmatter keys we read but cannot store.
 *
 * This exists because of fact 3 in the header: an unmapped key written into the
 * file would be deleted by the next canonicalise, with no error anywhere. So the
 * importer states the loss instead.
 */
export function reportUnmappedFrontmatter(data, mapped, note) {
    const left = Object.keys(data).filter((key) => !mapped.includes(key));
    for (const key of left) {
        note('unconverted', `frontmatter-${key.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`, `Frontmatter key \`${key}\` has no equivalent in our frontmatter and was not carried over.`);
    }
    return left;
}
/** Build the frontmatter block, in the order the schema serialises it. */
export function frontmatterBlock(fm) {
    const lines = ['---', `title: ${yamlScalar(fm.title || 'Untitled')}`];
    if (fm.description)
        lines.push(`description: ${yamlScalar(fm.description)}`);
    if (fm.section)
        lines.push(`section: ${yamlScalar(fm.section)}`);
    if (fm.group)
        lines.push(`group: ${yamlScalar(fm.group)}`);
    // Not truncated. A fractional position is how a page gets placed between two
    // others, and rounding it collapses pages that were deliberately ordered.
    if (fm.order !== undefined)
        lines.push(`order: ${fm.order}`);
    if (fm.draft)
        lines.push('draft: true');
    if (fm.redirectFrom && fm.redirectFrom.length > 0) {
        lines.push('redirect_from:');
        for (const path of fm.redirectFrom)
            lines.push(`  - ${yamlScalar(path)}`);
    }
    lines.push('---', '');
    return lines.join('\n');
}
/**
 * Remove the common leading indentation from a block.
 *
 * Not cosmetic. Four spaces of indentation is a CODE BLOCK in Markdown, and
 * both MkDocs admonitions and JSX children arrive indented, so importing them
 * verbatim turns prose into code. Measured: `canonicalise` turns a four-space
 * indented paragraph into a fenced code block.
 */
export function dedent(text) {
    const lines = text.replace(/\t/g, '    ').split('\n');
    const indents = lines
        .filter((line) => line.trim().length > 0)
        .map((line) => line.length - line.trimStart().length);
    if (indents.length === 0)
        return text.trim();
    const common = Math.min(...indents);
    return lines
        .map((line) => (line.trim().length === 0 ? '' : line.slice(common)))
        .join('\n')
        .trim();
}
//# sourceMappingURL=import-notes.js.map